
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { products } from '../../mocks/products';
import Navbar from '../../components/feature/Navbar';

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showAddedToCart, setShowAddedToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const product = products.find(p => p.id === Number(id));

  // 複数の商品画像を生成
  const productImages = product ? [
    product.image,
    product.image.replace('seq=casual-product-', 'seq=casual-detail-1-'),
    product.image.replace('seq=casual-product-', 'seq=casual-detail-2-'),
    product.image.replace('seq=casual-product-', 'seq=casual-detail-3-'),
    product.image.replace('seq=casual-product-', 'seq=casual-detail-4-')
  ] : [];

  useEffect(() => {
    if (product) {
      setSelectedSize(product.sizes[0]);
      setSelectedColor(product.colors[0]);
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-light text-gray-900 mb-4">商品が見つかりません</h2>
          <button 
            onClick={() => navigate('/')}
            className="bg-black text-white px-6 py-3 text-sm font-medium tracking-wider hover:bg-gray-800 transition-colors"
          >
            ホームに戻る
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    setShowAddedToCart(true);
    setTimeout(() => setShowAddedToCart(false), 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-black transition-colors mb-8"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            商品一覧に戻る
          </button>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Side - Product Images */}
            <div className="lg:w-2/3">
              {/* Main Images - Vertical Layout */}
              <div className="space-y-4">
                {productImages.map((image, index) => (
                  <div key={index} className="relative bg-gray-50 rounded-lg overflow-hidden">
                    <img
                      src={image}
                      alt={`${product.name} - ${index + 1}`}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Product Info + Thumbnails */}
            <div className="lg:w-1/3">
              <div className="lg:sticky lg:top-24">
                {/* Thumbnail Gallery */}
                <div className="flex flex-row lg:flex-col gap-2 mb-8 overflow-x-auto lg:overflow-visible">
                  {productImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImageIndex === index 
                          ? 'border-black' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>

                {/* Product Info */}
                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wider mb-2">{product.category}</p>
                    <h1 className="text-2xl lg:text-3xl font-light text-gray-900 mb-4">{product.name}</h1>
                    <p className="text-2xl font-medium text-gray-900 mb-6">{product.price}</p>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <p className="text-gray-700 leading-relaxed text-sm">{product.description}</p>
                  </div>

                  {/* Detailed Size Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">サイズ詳細</h3>
                    <div className="text-sm text-gray-700 space-y-2">
                      {product.sizes.includes('S') && (
                        <div>
                          <strong>S:</strong> 総丈 100cm / 股上 36cm / 股下 67cm / ヒップ 46cm / ウエスト 33cm
                        </div>
                      )}
                      {product.sizes.includes('M') && (
                        <div>
                          <strong>M:</strong> 総丈 101cm / 股上 36cm / 股下 68cm / ヒップ 49cm / ウエスト 34cm
                        </div>
                      )}
                      {product.sizes.includes('L') && (
                        <div>
                          <strong>L:</strong> 総丈 102cm / 股上 37cm / 股下 69cm / ヒップ 52cm / ウエスト 36cm
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">伸縮性なし</p>
                    </div>
                  </div>

                  {/* Size Selection */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">サイズ</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 py-2 text-sm border transition-colors ${
                            selectedSize === size
                              ? 'border-black bg-black text-white'
                              : 'border-gray-300 text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Selection */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">カラー</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.colors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          className={`px-4 py-2 text-sm border transition-colors ${
                            selectedColor === color
                              ? 'border-black bg-black text-white'
                              : 'border-gray-300 text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quantity */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">数量</h3>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="text-lg font-medium px-4">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-10 h-10 border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>

                  {/* Add to Cart Button */}
                  <div className="pt-6">
                    <button
                      onClick={handleAddToCart}
                      className="w-full bg-black text-white py-4 text-sm font-medium tracking-wider hover:bg-gray-800 transition-colors mb-4"
                    >
                      カートに追加
                    </button>
                    
                    {showAddedToCart && (
                      <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded text-sm">
                        <i className="ri-check-line mr-2"></i>
                        カートに追加されました
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="border-t border-gray-200 pt-6 space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">素材</h4>
                      <p className="text-sm text-gray-700">{product.material}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">お手入れ方法</h4>
                      <p className="text-sm text-gray-700">{product.care}</p>
                    </div>
                  </div>

                  {/* Free Shipping Notice */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-truck-line text-blue-600 mr-2"></i>
                      <div>
                        <p className="text-sm font-medium text-blue-900">10,000円以上のご購入で</p>
                        <p className="text-lg font-bold text-blue-900">送料無料</p>
                        <p className="text-xs text-blue-700">※北海道・沖縄・離島は除きます</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
